import * as React from "react";
import { useContext, useEffect, useState } from "react";

import InfoIcon from "@mui/icons-material/Info";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import {
  Alert,
  Button,
  LinearProgress,
  Tooltip,
  useTheme,
} from "@mui/material";
import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import clsx from "clsx";
import { Clipboard, ExternalLink } from "react-feather";
import { Link } from "react-router-dom";
import { useParams } from "react-router-dom";
import mainStyles from "styles";

import {
  AdminRestriction,
  AdminRestrictionAllowList,
  AdminRestrictionFeature,
  AdminRestrictionQuota,
  AdminRestrictionTimeWindow,
} from "@safetyculture/s12-apis-connect-web/s12/restrictions/admin/v1/restrictions_pb";
import { RestrictionQuota } from "@safetyculture/s12-apis-connect-web/s12/restrictions/v1/common_pb";

import {
  getOrgRestrictions as getOrgRestrictionsApi,
  getUserRestrictions as getUserRestrictionsApi,
} from "api/endpoints/restrictions";
import { callOptionsHeaders } from "api/helpers";
import { NO_DISPLAY_DATA } from "constants/index";
import { SnackbarContext } from "context/snackbar-context";
import { toUUID } from "helpers/s12-helpers";
import { convertTimestampToDate, formatDateTimeDisplay } from "helpers/time";
import { UrlParamTypes } from "types/org-display";
import { UserUrlParamTypes } from "types/user-display";

type RestrictionsTabParams = {
  isUser: boolean;
};

const RestrictionsTab = ({ isUser }: RestrictionsTabParams) => {
  const { regionCode: userRegionCode, userId } = useParams<UserUrlParamTypes>();
  const { regionCode: orgRegionCode, roleId } = useParams<UrlParamTypes>();
  const [isLoading, setIsLoading] = useState(false);
  const [restrictions, setRestrictions] = useState<
    Array<[string, AdminRestriction]>
  >([]);

  const { copyToClipboard, showError } = useContext(SnackbarContext);

  const theme = useTheme();
  const classes = mainStyles();

  const getUserRestrictions = () => {
    setIsLoading(true);
    getUserRestrictionsApi(
      {
        userId: toUUID(userId),
        restrictionNames: [],
      },
      callOptionsHeaders(userRegionCode, userId)
    )
      .then((restrictionsResponse) => {
        const restrictions = restrictionsResponse.restrictions;
        //sort restrictions
        const sortedRestrictions = Object.entries(restrictions).sort(
          ([keyA], [keyB]) => keyA.localeCompare(keyB)
        );
        // get rid of empty restrictions
        const validRestrictions = sortedRestrictions.filter(
          (r) => r[1].restriction.case !== undefined
        );
        setRestrictions(validRestrictions);
      })
      .catch((err) => {
        showError(err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const getOrgRestrictions = () => {
    setIsLoading(true);
    getOrgRestrictionsApi(
      {
        organizationId: toUUID(roleId),
        restrictionNames: [],
      },
      callOptionsHeaders(orgRegionCode, roleId)
    )
      .then((restrictionsResponse) => {
        const restrictions = restrictionsResponse.restrictions;
        //sort restrictions
        const sortedRestrictions = Object.entries(restrictions).sort(
          ([keyA], [keyB]) => keyA.localeCompare(keyB)
        );
        // get rid of empty restrictions
        const validRestrictions = sortedRestrictions.filter(
          (r) => r[1].restriction.case !== undefined
        );
        setRestrictions(validRestrictions);
      })
      .catch((err) => {
        showError(err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const DisplayAllowedIds = (ids: string[]) => {
    return ids.map((id) => {
      return (
        <Box key={id}>
          <span style={{ paddingRight: "5px" }}> {id} </span>
          <Clipboard
            color={theme.palette.text.primary}
            size={"1rem"}
            className={clsx(classes.positionIcon, classes.clickable)}
            onClick={() => copyToClipboard(id)}
          />
          &nbsp;
          <Link
            to={{
              pathname: `/search/doc/template_${id}`,
            }}
            target="_blank"
            style={{ textDecoration: "none", color: "inherit" }}
          >
            <ExternalLink
              color={theme.palette.text.primary}
              size={"1rem"}
              className={clsx(classes.positionIcon, classes.clickable)}
            />
          </Link>
          <br />
        </Box>
      );
    });
  };

  const DisplayAllowList = (restriction: AdminRestrictionAllowList) => {
    if (!restriction.userRestriction) return null;

    return (
      <TableContainer>
        <Table
          size="small"
          padding="none"
          sx={{
            [`& .${tableCellClasses.root}`]: {
              borderBottom: "none",
            },
          }}
        >
          <TableBody>
            <TableRow key="seat-type">
              <TableCell colSpan={2}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                  }}
                >
                  <span>
                    <b>Allowed IDs</b>
                  </span>
                  <Box
                    sx={{
                      textAlign: "right",
                      fontFamily: "monospace",
                      fontSize: "small",
                    }}
                    paddingLeft={"5px"}
                    data-testid={"allowed-ids"}
                  >
                    {DisplayAllowedIds(restriction.userRestriction.allowedIds)}
                  </Box>
                </Box>
              </TableCell>
            </TableRow>
            <TableRow key="total-quota">
              <TableCell colSpan={2}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <span>
                    <b>Total List Capacity</b>
                    <Tooltip title="This is the maximum capacity of the allow list for the user.">
                      <IconButton size="small" sx={{ padding: "0px" }}>
                        <InfoIcon />
                      </IconButton>
                    </Tooltip>
                  </span>
                  <span data-testid={"total-allowed-ids-capacity"}>
                    <b>{restriction.userRestriction?.maxAllowedIds}</b>
                  </span>
                </Box>
              </TableCell>
            </TableRow>
            <TableRow key="default-seat-quota">
              <TableCell colSpan={2}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <span>Capacity Used</span>
                  <span data-testid={"capacity-used"}>
                    {restriction.userRestriction.allowedIds.length}
                  </span>
                </Box>
              </TableCell>
            </TableRow>
            <TableRow key="default-seat-quota">
              <TableCell colSpan={2}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <span>Capacity Remaining</span>
                  <span data-testid={"capacity-remaining"}>
                    {restriction.defaultCapacity -
                      restriction.userRestriction.allowedIds.length}
                  </span>
                </Box>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const DisplayDetailedRestriction = (restriction: AdminRestriction) => {
    switch (restriction.restriction.case) {
      case "allowList":
        return DisplayAllowList(
          restriction.restriction.value as AdminRestrictionAllowList
        );
    }

    return null;
  };

  const displayQuotaResetDate = (quota: RestrictionQuota | undefined) => {
    if (!quota) {
      return NO_DISPLAY_DATA;
    }

    if (quota.resetTime) {
      return (
        <>{formatDateTimeDisplay(convertTimestampToDate(quota.resetTime))}</>
      );
    } else {
      return NO_DISPLAY_DATA;
    }
  };

  const DisplayRestrictionSummary = (
    restriction: AdminRestriction,
    open: boolean,
    setOpen: React.Dispatch<React.SetStateAction<boolean>>
  ) => {
    switch (restriction.restriction.case) {
      case "allowList":
        const allowListRestriction = restriction.restriction
          .value as AdminRestrictionAllowList;
        return (
          <>
            <Box>
              <IconButton
                aria-label="expand row"
                size="small"
                onClick={() => setOpen(!open)}
              >
                {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
              </IconButton>
              <Button
                variant="text"
                onClick={() => setOpen(!open)}
                data-testid={"allow-list-summary-button"}
              >
                {allowListRestriction.userRestriction?.allowedIds.length}/
                {allowListRestriction.userRestriction?.maxAllowedIds} allowed
                ids
              </Button>
            </Box>
          </>
        );
      case "timeWindow":
        const timeWindowRestriction = restriction.restriction
          .value as AdminRestrictionTimeWindow;
        const durationSeconds =
          timeWindowRestriction.userRestriction?.maxTimeWindow?.seconds;
        return (
          <>
            {durationSeconds ? Number(durationSeconds) / (3600 * 24) : "--"}
            &nbsp;days
          </>
        );
      case "feature":
        const featureRestriction = restriction.restriction
          .value as AdminRestrictionFeature;
        return (
          <>
            {featureRestriction.userRestriction?.isRestricted
              ? "restricted"
              : "not restricted"}
          </>
        );
      case "quota":
        const quotaRestriction = restriction.restriction
          .value as AdminRestrictionQuota;
        const userQuota = quotaRestriction?.userRestriction;
        return (
          <>
            {userQuota?.remaining}/{userQuota?.limit} {userQuota?.unit}{" "}
            remaining | resets at {displayQuotaResetDate(userQuota)}
          </>
        );
    }
  };

  function Row(props: { key: string; row: [string, AdminRestriction] }) {
    const [open, setOpen] = React.useState(false);
    const { row } = props;
    const [name, restriction] = row;

    return (
      <React.Fragment>
        {!restriction.restriction.case ? null : (
          <TableRow>
            {!["allowList"].includes(restriction.restriction.case || "") ? (
              <TableCell />
            ) : (
              <TableCell>
                <IconButton
                  aria-label="expand row"
                  size="small"
                  onClick={() => setOpen(!open)}
                  data-testid={"left-expand-button"}
                >
                  {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                </IconButton>
              </TableCell>
            )}
            <TableCell component="th" scope="row">
              {name.replace("RESTRICTION_NAME_", "")}
            </TableCell>
            <TableCell align="left">{restriction.restriction.case}</TableCell>
            <TableCell align="left">{restriction.featureFlagName}</TableCell>
            <TableCell align="left" width={"200px"}>
              {DisplayRestrictionSummary(restriction, open, setOpen)}
              <Collapse in={open} timeout="auto" unmountOnExit>
                {DisplayDetailedRestriction(restriction)}
              </Collapse>
            </TableCell>
          </TableRow>
        )}
      </React.Fragment>
    );
  }

  useEffect(() => {
    if (isUser) getUserRestrictions();
    else getOrgRestrictions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {!isLoading && restrictions.length == 0 ? (
        <Alert
          severity="info"
          variant="standard"
          style={{ margin: "2px" }}
          data-testid={"no-restriction-alert"}
        >
          User has no applicable restrictions.
        </Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table aria-label="collapsible table">
            <colgroup>
              <col style={{ minWidth: "20px" }} />
              <col style={{ minWidth: "200px" }} />
              <col style={{ minWidth: "50px" }} />
              <col style={{ minWidth: "100px" }} />
              <col style={{ minWidth: "550px" }} />
            </colgroup>
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell>Restriction</TableCell>
                <TableCell align="left">Type</TableCell>
                <TableCell align="left">Feature Flag</TableCell>
                <TableCell align="left">Details</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow key="seat-type">
                  <TableCell colSpan={5}>
                    <LinearProgress />
                  </TableCell>
                </TableRow>
              ) : (
                restrictions.map((restriction) => (
                  <Row key={restriction[0]} row={restriction} />
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </>
  );
};
export default RestrictionsTab;
